import type { Ref } from 'vue';

import { computed, provide } from 'vue';
import { useRoute } from 'vue-router';

import * as routeQueryHelpers from '@/helpers/routeQuery';
import * as injectionKeys from '@/constants/injectionKeys';
import { defCurPage, defPageSize } from '@/constants/routeQuery';

export interface DefCommonRouteQuery {
  queryCurPage?: number;
  queryPageSize?: number;
  queryCustomer?: string;
  queryDate?: Date;
  queryDates?: [Date, Date];
  queryStates?: string[];
  querySearch?: string;
  queryStatus?: number;
  queryDrivers?: number[];
  queryDriver?: number;
  queryDivision?: string;
  queryStatuses?: string[];
  queryAttributes?: string[];
  queryJobId?: number;
  queryJobDate?: string;
  queryJobIds?: number[];
  querySuburb?: string;
  querySuburbId?: number;
  queryPostCode?: number;
  queryDistance?: number;
  queryFilters?: string[];
  queryCustomers?: string[];
  queryServices?: string[];
  querySerial?: string;
  queryAddress?: string;
  queryCombined?: string;
  queryDateType?: string;
  queryOnly920Driver?: boolean;
  queryExclude920Driver?: boolean;
  queryFromWarehouse?: boolean;
  queryToWarehouse?: boolean;
  queryPayOnHold?: boolean;
}
export interface CommonRouteQueryComposable {
  // or it can be same ComputedRef<number>
  queryCurPage: Readonly<Ref<number>>;
  queryPageSize: Readonly<Ref<number>>;
  queryCustomer: Readonly<Ref<string | undefined>>;
  queryDate: Readonly<Ref<Date | undefined>>;
  queryDates: Readonly<Ref<[Date, Date] | undefined>>;
  queryStates: Readonly<Ref<string[] | undefined>>;
  querySearch: Readonly<Ref<string | undefined>>;
  queryStatus: Readonly<Ref<number | undefined>>;
  queryDrivers: Readonly<Ref<number[] | undefined>>;
  queryDriver: Readonly<Ref<number | undefined>>;
  queryDivision: Readonly<Ref<string | undefined>>;
  queryStatuses: Readonly<Ref<string[] | undefined>>;
  queryAttributes: Readonly<Ref<string[] | undefined>>;
  queryJobId: Readonly<Ref<number | undefined>>;
  queryJobDate: Readonly<Ref<string | undefined>>;
  queryJobIds: Readonly<Ref<number[] | undefined>>;
  querySuburb: Readonly<Ref<string | undefined>>;
  querySuburbId: Readonly<Ref<number | undefined>>;
  queryPostCode: Readonly<Ref<number | undefined>>;
  queryDistance: Readonly<Ref<number | undefined>>;
  queryFilters: Readonly<Ref<string[] | undefined>>;
  queryCustomers: Readonly<Ref<string[] | undefined>>;
  queryServices: Readonly<Ref<string[] | undefined>>;
  querySerial: Readonly<Ref<string | undefined>>;
  queryAddress: Readonly<Ref<string | undefined>>;
  queryCombined: Readonly<Ref<string | undefined>>;
  queryDateType: Readonly<Ref<string | undefined>>;
  queryOnly920Driver: Readonly<Ref<boolean>>;
  queryExclude920Driver: Readonly<Ref<boolean>>;
  queryFromWarehouse: Readonly<Ref<boolean>>;
  queryToWarehouse: Readonly<Ref<boolean>>;
  queryPayOnHold: Readonly<Ref<boolean | undefined>>;
}

export default function useRouteQuery(
  defRouteQuery: DefCommonRouteQuery,
): CommonRouteQueryComposable {
  if (defRouteQuery.queryCurPage == null) {
    defRouteQuery.queryCurPage = defCurPage;
  }
  if (defRouteQuery.queryPageSize == null) {
    defRouteQuery.queryPageSize = defPageSize;
  }
  const _defCurPage = defRouteQuery.queryCurPage;
  const _defPageSize = defRouteQuery.queryPageSize;
  const _defCustomer = defRouteQuery.queryCustomer;
  const _defDate = defRouteQuery.queryDate;
  const _defDates = defRouteQuery.queryDates;
  const _defStates = defRouteQuery.queryStates;
  const _defSearch = defRouteQuery.querySearch;
  const _defStatus = defRouteQuery.queryStatus;
  const _defDrivers = defRouteQuery.queryDrivers;
  const _defDriver = defRouteQuery.queryDriver;
  const _defDivision = defRouteQuery.queryDivision;
  const _defStatuses = defRouteQuery.queryStatuses;
  const _defAttributes = defRouteQuery.queryAttributes;
  const _defJobId = defRouteQuery.queryJobId;
  const _defJobDate = defRouteQuery.queryJobDate;
  const _defJobIds = defRouteQuery.queryJobIds;
  const _defSuburb = defRouteQuery.querySuburb;
  const _defSuburbId = defRouteQuery.querySuburbId;
  const _defPostCode = defRouteQuery.queryPostCode;
  const _defDistance = defRouteQuery.queryDistance;
  const _defFilters = defRouteQuery.queryFilters;
  const _defCustomers = defRouteQuery.queryCustomers;
  const _defServices = defRouteQuery.queryServices;
  const _defSerial = defRouteQuery.querySerial;
  const _defAddress = defRouteQuery.queryAddress;
  const _defCombined = defRouteQuery.queryCombined;
  const _defDateType = defRouteQuery.queryDateType;
  if (defRouteQuery.queryOnly920Driver == null) {
    defRouteQuery.queryOnly920Driver = false;
  }
  if (defRouteQuery.queryExclude920Driver == null) {
    defRouteQuery.queryExclude920Driver = false;
  }
  if (defRouteQuery.queryFromWarehouse == null) {
    defRouteQuery.queryFromWarehouse = false;
  }
  if (defRouteQuery.queryToWarehouse == null) {
    defRouteQuery.queryToWarehouse = false;
  }
  const _defOnly920Driver = defRouteQuery.queryOnly920Driver;
  const _defExclude920Driver = defRouteQuery.queryExclude920Driver;
  const _defFromWarehouse = defRouteQuery.queryFromWarehouse;
  const _defToWarehouse = defRouteQuery.queryToWarehouse;
  const _defPayOnHold = defRouteQuery.queryPayOnHold;

  const route = useRoute();

  const queryCurPage = computed<number>(() => {
    return (
      routeQueryHelpers.getQueryNumberValue(route.query.page, _defCurPage) ??
      _defCurPage
    );
  });

  const queryPageSize = computed<number>(() => {
    return (
      routeQueryHelpers.getQueryNumberValue(
        route.query.page_size,
        _defPageSize,
      ) ?? _defPageSize
    );
  });

  const queryCustomer = computed<string | undefined>(() => {
    return routeQueryHelpers.getQueryStringValue(
      route.query.customer,
      _defCustomer,
    );
  });

  const queryDate = computed<Date | undefined>(() => {
    return routeQueryHelpers.getQueryDateValue(route.query.date, _defDate);
  });

  const queryDates = computed<[Date, Date] | undefined>(() => {
    return routeQueryHelpers.getQueryDatePairValue(
      route.query.dates,
      _defDates,
    );
  });

  const queryStates = computed<string[] | undefined>(() => {
    return routeQueryHelpers.getQueryStringListValue(
      route.query.states,
      _defStates,
    );
  });

  const querySearch = computed<string | undefined>(() => {
    return routeQueryHelpers.getQueryStringValue(
      route.query.search,
      _defSearch,
    );
  });

  const queryStatus = computed<number | undefined>(() => {
    return routeQueryHelpers.getQueryNumberValue(
      route.query.status,
      _defStatus,
    );
  });

  const queryDrivers = computed<number[] | undefined>(() => {
    return routeQueryHelpers.getQueryNumberListValue(
      route.query.drivers,
      _defDrivers,
    );
  });

  const queryDriver = computed<number | undefined>(() => {
    return routeQueryHelpers.getQueryNumberValue(
      route.query.driver,
      _defDriver,
    );
  });

  const queryDivision = computed<string | undefined>(() => {
    return routeQueryHelpers.getQueryStringValue(
      route.query.division,
      _defDivision,
    );
  });

  const queryStatuses = computed<string[] | undefined>(() => {
    return routeQueryHelpers.getQueryStringListValue(
      route.query.statuses,
      _defStatuses,
    );
  });

  const queryAttributes = computed<string[] | undefined>(() => {
    return routeQueryHelpers.getQueryStringListValue(
      route.query.attributes,
      _defAttributes,
    );
  });

  const queryJobId = computed<number | undefined>(() => {
    return routeQueryHelpers.getQueryNumberValue(route.query.job_id, _defJobId);
  });

  const queryJobDate = computed<string | undefined>(() => {
    return routeQueryHelpers.getQueryStringValue(
      route.query.job_date,
      _defJobDate,
    );
  });

  const queryJobIds = computed<number[] | undefined>(() => {
    return routeQueryHelpers.getQueryNumberListValue(
      route.query.job_ids,
      _defJobIds,
    );
  });

  const querySuburb = computed<string | undefined>(() => {
    return routeQueryHelpers.getQueryStringValue(
      route.query.suburb,
      _defSuburb,
    );
  });

  const querySuburbId = computed<number | undefined>(() => {
    return routeQueryHelpers.getQueryNumberValue(
      route.query.suburb_id,
      _defSuburbId,
    );
  });

  const queryPostCode = computed<number | undefined>(() => {
    return routeQueryHelpers.getQueryNumberValue(
      route.query.postcode,
      _defPostCode,
    );
  });

  const queryDistance = computed<number | undefined>(() => {
    return routeQueryHelpers.getQueryNumberValue(
      route.query.distance,
      _defDistance,
    );
  });

  const queryFilters = computed<string[] | undefined>(() => {
    return routeQueryHelpers.getQueryStringListValue(
      route.query.filters,
      _defFilters,
    );
  });

  const queryCustomers = computed<string[] | undefined>(() => {
    return routeQueryHelpers.getQueryStringListValue(
      route.query.customers,
      _defCustomers,
    );
  });

  const queryServices = computed<string[] | undefined>(() => {
    return routeQueryHelpers.getQueryStringListValue(
      route.query.services,
      _defServices,
    );
  });

  const querySerial = computed<string | undefined>(() => {
    return routeQueryHelpers.getQueryStringValue(
      route.query.serial,
      _defSerial,
    );
  });

  const queryAddress = computed<string | undefined>(() => {
    return routeQueryHelpers.getQueryStringValue(
      route.query.address,
      _defAddress,
    );
  });

  const queryCombined = computed<string | undefined>(() => {
    return routeQueryHelpers.getQueryStringValue(
      route.query.combined,
      _defCombined,
    );
  });

  const queryDateType = computed<string | undefined>(() => {
    return routeQueryHelpers.getQueryStringValue(
      route.query.date_type,
      _defDateType,
    );
  });

  const queryOnly920Driver = computed<boolean>(() => {
    return (
      routeQueryHelpers.getQueryBooleanValue(
        route.query.only_920_driver,
        _defOnly920Driver,
      ) ?? _defOnly920Driver
    );
  });

  const queryExclude920Driver = computed<boolean>(() => {
    return (
      routeQueryHelpers.getQueryBooleanValue(
        route.query.exclude_920_driver,
        _defExclude920Driver,
      ) ?? _defExclude920Driver
    );
  });

  const queryFromWarehouse = computed<boolean>(() => {
    return (
      routeQueryHelpers.getQueryBooleanValue(
        route.query.from_warehouse,
        _defFromWarehouse,
      ) ?? _defFromWarehouse
    );
  });

  const queryToWarehouse = computed<boolean>(() => {
    return (
      routeQueryHelpers.getQueryBooleanValue(
        route.query.to_warehouse,
        _defToWarehouse,
      ) ?? _defToWarehouse
    );
  });

  const queryPayOnHold = computed<boolean | undefined>(() => {
    return (
      routeQueryHelpers.getQueryBooleanValue(
        route.query.pay_on_hold,
        _defPayOnHold,
      ) ?? _defPayOnHold
    );
  });

  provide(injectionKeys.queryCurPageKey, queryCurPage);
  provide(injectionKeys.queryPageSizeKey, queryPageSize);
  provide(injectionKeys.queryCustomerKey, queryCustomer);
  provide(injectionKeys.queryDateKey, queryDate);
  provide(injectionKeys.queryDatesKey, queryDates);
  provide(injectionKeys.queryStatesKey, queryStates);
  provide(injectionKeys.querySearchKey, querySearch);
  provide(injectionKeys.queryStatusKey, queryStatus);
  provide(injectionKeys.queryDriversKey, queryDrivers);
  provide(injectionKeys.queryDriverKey, queryDriver);
  provide(injectionKeys.queryDivisionKey, queryDivision);
  provide(injectionKeys.queryStatusesKey, queryStatuses);
  provide(injectionKeys.queryAttributesKey, queryAttributes);
  provide(injectionKeys.queryJobIdKey, queryJobId);
  provide(injectionKeys.queryJobDateKey, queryJobDate);
  provide(injectionKeys.queryJobIdsKey, queryJobIds);
  provide(injectionKeys.querySuburbKey, querySuburb);
  provide(injectionKeys.querySuburbIdKey, querySuburbId);
  provide(injectionKeys.queryPostCodeKey, queryPostCode);
  provide(injectionKeys.queryDistanceKey, queryDistance);
  provide(injectionKeys.queryFiltersKey, queryFilters);
  provide(injectionKeys.queryCustomersKey, queryCustomers);
  provide(injectionKeys.queryServicesKey, queryServices);
  provide(injectionKeys.querySerialKey, querySerial);
  provide(injectionKeys.queryAddressKey, queryAddress);
  provide(injectionKeys.queryCombinedKey, queryCombined);
  provide(injectionKeys.queryDateTypeKey, queryDateType);
  provide(injectionKeys.queryOnly920DriverKey, queryOnly920Driver);
  provide(injectionKeys.queryExclude920DriverKey, queryExclude920Driver);
  provide(injectionKeys.queryFromWarehouseKey, queryFromWarehouse);
  provide(injectionKeys.queryToWarehouseKey, queryToWarehouse);

  return {
    queryCurPage,
    queryPageSize,
    queryCustomer,
    queryDate,
    queryDates,
    queryStates,
    querySearch,
    queryStatus,
    queryDrivers,
    queryDriver,
    queryDivision,
    queryStatuses,
    queryAttributes,
    queryJobId,
    queryJobDate,
    queryJobIds,
    querySuburb,
    querySuburbId,
    queryPostCode,
    queryDistance,
    queryFilters,
    queryCustomers,
    queryServices,
    querySerial,
    queryAddress,
    queryCombined,
    queryDateType,
    queryOnly920Driver,
    queryExclude920Driver,
    queryFromWarehouse,
    queryToWarehouse,
    queryPayOnHold,
  };
}
